import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '../../shared/services/auth/auth.service';
import {
  SupplierChannel,
  SupplierSurveyStartChannel,
  Survey,
} from '../../shared/interfaces/survey.interface';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { SocketService } from '../../shared/services/socket/socket.service';
import { Subscription } from 'rxjs';
import { Constants } from '../constants/manage-suppliers-constants';
import {
  IntentInterface,
  SupplierInterface,
} from '../interfaces/manage-suppliers.interface';

@Component({
  selector: 'ps-ui-manage-suppliers',
  templateUrl: './manage-suppliers.component.html',
  styleUrls: ['./manage-suppliers.component.css'],
})
export class ManageSuppliersComponent implements OnInit, OnDestroy {
  @Input() surveyId: number | undefined;
  @Input() survey: Survey = {} as Survey;
  private _subscriptions: Subscription = new Subscription();
  public suppliers: SupplierInterface[] = [];
  public isAdvanceStatsEnable: boolean = false;
  public enableAccordianMenu: boolean = false;
  public enableSupplierSummary: boolean = false;
  public operator?: string = '';
  constructor(
    private _buyerApiService: BuyerApiService,
    private _auth: AuthService,
    private _socketService: SocketService
  ) {}

  ngOnInit(): void {
    this.surveyId = this.surveyId ? this.surveyId : 0;
    this.enableSupplierSummary = true;
    this._getSuppliers(this.surveyId);
    this.operator = this._auth.user?.operatorAcssLvls;
    this._setVarriables();

    const supplierSurveyStartChannelV3$ =
      this._socketService.supplierSurveyStartChannelV3$.subscribe(
        (data: SupplierSurveyStartChannel) => {
          this._updateSurveyStartWithDataReceived(data);
        }
      );
    this._subscriptions.add(supplierSurveyStartChannelV3$);

    const supplierChannel$ = this._socketService.supplierChannel$.subscribe(
      (data: SupplierChannel) => {
        this._updateSupplierChannelWithDataReceived(data);
      }
    );
    this._subscriptions.add(supplierChannel$);
  }
  private _updateSupplierChannelWithDataReceived(data: SupplierChannel) {
    if (this.surveyId == data.surveyId && this.suppliers) {
      this.suppliers = this.suppliers.map((sup) =>
        sup.suppliers[0].id === data.supplierId
          ? { ...sup, ...data.supplier }
          : sup
      );
    }
  }

  private _updateSurveyStartWithDataReceived(data: SupplierSurveyStartChannel) {
    if (this.surveyId == data.surveyId && this.suppliers) {
      this.suppliers = this.suppliers.map((sup) =>
        sup.suppliers[0].id === data.supplierId
          ? {
              ...sup,
              srv_start_count: data.startSurveyCount,
              last_start_date: String(data.lastSurveyStartDate),
            }
          : sup
      );
    }
  }

  private _setVarriables() {
    if (
      (this._auth.buyerConfig?.advanceStats && this._auth.user?.cmp) ||
      this.operator != 'none'
    ) {
      this.isAdvanceStatsEnable = true;
    }
    if (
      (this._auth.buyerConfig?.supplierAccordian && this._auth.user?.cmp) ||
      this.operator != 'none'
    ) {
      this.enableAccordianMenu = true;
    }
  }

  private _getSuppliers(surveyId: number) {
    const getSuppliers$ = this._buyerApiService
      .getSuppliers(
        surveyId,
        'view',
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        this.enableSupplierSummary
      )
      .subscribe((response: SupplierInterface[]) => {
        this.suppliers = response;
        this.suppliers = this.suppliers.filter((supplier) => {
          return (
            supplier.completes_goal ||
            supplier.srv_start_count ||
            this.survey.samples_name === Constants.SURVEY_SAMPLE_NAME
          );
        });
        this._mapIntentIcon(this.suppliers);
        this._mapSuppliersGroupRef(this.suppliers);
        this._mapSupplierDisplayName(this.suppliers);
      });
    this._subscriptions.add(getSuppliers$);
  }

  private _mapIntentIcon(suppliers: SupplierInterface[]) {
    suppliers.forEach((sup) => {
      const intent = sup.intent
        ? sup.intent
        : {
            intent_count: null,
            intent_not_set: [],
            intent_denied: [],
            intent_accepted: [],
            child_intent_stats: {},
          };
      sup.intentIcon = this.getIntentIcon(intent);
    });
  }

  private _mapSuppliersGroupRef(suppliers: SupplierInterface[]) {
    suppliers.forEach((sup) => {
      if (!sup.group_ref) {
        sup.group_ref = sup.suppliers[0].id;
        sup.isGrouped = false;
        sup.group_name = sup.suppliers[0].name;
      } else {
        sup.isGrouped = true;
      }
    });
  }

  //this function will set the supplier field name as name or group name if group ref exists
  private _mapSupplierDisplayName(suppliers: SupplierInterface[]) {
    suppliers.forEach((supplier) => {
      supplier.name = supplier.group_ref
        ? supplier.group_name
        : supplier.suppliers[0].name;
    });
  }

  public getIntentIcon(intent: IntentInterface) {
    const newIntent = this._getIntent(intent);
    const [intentIcon, ids] = this._getIntentIconDetail(newIntent);
    return [intentIcon, ids];
  }

  private _getIntent(intent: IntentInterface) {
    if (
      intent?.child_intent_stats &&
      Object.keys(intent.child_intent_stats).length > 0 &&
      this._intentKeysNotBlank(intent.child_intent_stats)
    ) {
      return intent.child_intent_stats;
    } else if (intent && this._intentKeysNotBlank(intent)) {
      return intent;
    } else {
      return null;
    }
  }

  private _intentKeysNotBlank(intentObject: any) {
    return Object.keys(intentObject).some((intentKey: string) => {
      return (
        Array.isArray(intentObject[intentKey]) &&
        intentObject[intentKey].length > 0
      );
    });
  }

  private _getIntentIconDetail(
    intent: IntentInterface | { [key: string]: any } | null
  ) {
    let intentIcon = Constants.SUPPLIER_INTENT_ICON.BLACK_QUESTION;
    let ids: [] = [];

    if (!intent) {
      return [intentIcon, ids];
    }

    const intentNotSet = intent.intent_not_set ? intent.intent_not_set : [];
    const intentDenied = intent.intent_denied;
    const intentAccepted = intent.intent_accepted;

    if (intentNotSet.length === 0) {
      if (intentDenied.length === 0 && intentAccepted.length > 0) {
        intentIcon = Constants.SUPPLIER_INTENT_ICON.GREEN_CHECK;
      } else if (intentDenied.length > 0 && intentAccepted.length === 0) {
        intentIcon = Constants.SUPPLIER_INTENT_ICON.RED_CROSS;
      } else if (
        intentDenied.length > 0 &&
        intentAccepted.length > 0 &&
        this.operator != 'none'
      ) {
        intentIcon = Constants.SUPPLIER_INTENT_ICON.YELLOW_CROSS;
        ids = intentDenied;
      }
    } else {
      if (this._getIntIconNotSet(intentDenied, intentAccepted)) {
        intentIcon = Constants.SUPPLIER_INTENT_ICON.YELLOW_EXCLAMATION;
        ids = intentNotSet;
      }
    }
    return [intentIcon, ids];
  }

  private _getIntIconNotSet(intentDenied: [], intentAccepted: []) {
    if (
      intentDenied.length == 0 &&
      intentAccepted.length > 0 &&
      this.operator !== 'none'
    ) {
      return true;
    }
    return false;
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}
